import React, { useCallback, useEffect, useState, useRef } from "react";
import App from "app";
import ReactFlow, {
    Connection,
    Edge,
    ConnectionLineType,
    Controls,
    NodeChange,
    EdgeChange,
    addEdge,
    Background,
    BackgroundVariant,
    SelectionMode,
    useReactFlow
} from "reactflow";
import { useParams } from "react-router";
import FlowEmptyState from "./flow-empty-state";
import { NODE_TYPES } from "./flow.constants";
import {
    useAppStatus,
    useEdgeHighlighting,
    useFlowData,
    useFlowLayout,
    useNodeSearch,
    useNodeSelection,
    useNodeStatistics,
    useSaveNodePosition
} from "../helpers/flow.hooks";
import { getDefaultEdgeOptions } from "../helpers/flow.utils";
import { useAppSelector, useAppDispatch } from "src/stores/redux/hooks";
import {
    getNodes,
    getEdges,
    getIsFetchingNodes,
    getIsEmpty,
    getAppFQN,
    getCurrentFlowId,
    getSelectedNode,
    getNamespace,
    getNeedsRefresh
} from "../helpers/selectors";
import {
    onNodesChangeAction,
    onEdgesChangeAction,
    setEdgesAction,
    closeAllSidebarsAction,
    clearStreamPreviewDataAction,
    setStreamGraphPreviewVisibleAction,
    setSelectedNodeAction,
    setIsRightSidebarOpenAction,
    deleteNodeAction,
    clearRefreshAction,
    triggerRefreshAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { clearByApplication } from "app/components/flow/designer/nodesPositionStorage";
import streamDataLoader from "../stream-preview/stream-data-loader";
import { useHelperLines } from "../helpers/useHelperLines";
import { HelperLines } from "../components/HelperLines";
import TopRightPanel from "./panels/top-right-panel";
import BottomLeftPanel from "./panels/bottom-left-panel";
import { Box } from "@mui/material";
import StriimCircularProgress from "../../../apps/pages/manage-striim/connection-profiles/components/circular-progress/circular-progress";
import sxClasses from "../flowdesigner.styles";
import { getSavedSelection, handleCopyToApp, isTempNode } from "../helpers/node.utils";
import api from "core/api/api";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import { FlowUrlProps } from "../flowdesigner";
import { navigateToFlow } from "../helpers/navigation.utils";
import store from "src/stores/redux/store";
import { selectIsSidebarOpen } from "src/stores/redux/features/striim-ai/striim-ai.slice";

const SIDEBAR_TRANSITION_MS = 300;

type FlowProps = { promptIfUnsavedChanged?: () => Promise<boolean> };

const Flow: React.FC<FlowProps> = ({ promptIfUnsavedChanged }) => {
    const { appName, flowName, nodeName } = useParams<FlowUrlProps>();
    const dispatch = useAppDispatch();

    const nodes = useAppSelector(getNodes);
    const edges = useAppSelector(getEdges);
    const isFetchingNodes = useAppSelector(getIsFetchingNodes);
    const isEmpty = useAppSelector(getIsEmpty);
    const appFQN = useAppSelector(getAppFQN);
    const currentFlowId = useAppSelector(getCurrentFlowId);
    const selectedNode = useAppSelector(getSelectedNode);
    const namespace = useAppSelector(getNamespace);
    const needsRefresh = useAppSelector(getNeedsRefresh);

    const appStatus = useAppStatus();
    const { onNodeClick, closeRightSidebar } = useNodeSelection(appName, flowName);
    const saveNodeposition = useSaveNodePosition();
    const handleNodeSearchWrapper = useNodeSearch();
    const { currentLayout, toggleLayout } = useFlowLayout();
    const { onNodeMouseEnter, onNodeMouseLeave } = useEdgeHighlighting(appStatus);
    const { helperLines, onNodeChangeWithHelperLines } = useHelperLines();

    // Multi-selection mode: when active, dragging on the pane draws a selection box
    // (instead of panning). Toggled via the button in the top-right panel.
    const [isMultiSelectActive, setIsMultiSelectActive] = useState(false);
    const toggleMultiSelect = useCallback(() => setIsMultiSelectActive(prev => !prev), []);

    const { fetchAndPopulateNodes } = useFlowData();
    useNodeStatistics(appStatus);

    // for ai sidebar
    const { fitView } = useReactFlow();
    const chatbotOpen = useAppSelector(selectIsSidebarOpen);

    useEffect(() => {
        if (!getNodes(store.getState()).length) return;
        const timer = setTimeout(() => fitView({ duration: 300 }), SIDEBAR_TRANSITION_MS);
        return () => clearTimeout(timer);
    }, [chatbotOpen, fitView]);

    // Determine if we're viewing a specific flow or the main app view
    const isViewingSpecificFlow = currentFlowId && currentFlowId !== appFQN;

    // Set the current namespace for the API (required for name validation in sidebar)
    // This is what the original Marionette flow designer does in designerFactory.js line 36
    useEffect(() => {
        if (namespace) api.setCurrentNamespace(namespace);
    }, [namespace]);

    useEffect(() => {
        if (!needsRefresh || !appFQN) return;
        dispatch(clearRefreshAction());
        clearByApplication(appFQN);
        (async () => {
            await fetchAndPopulateNodes();
            const currentSelectedNode = getSelectedNode(store.getState());
            const metaObjectId = currentSelectedNode?.data?.metaObjectId;
            if (!metaObjectId) return;

            try {
                const metaObject = await metaStoreService.findById(metaObjectId);
                dispatch(
                    setSelectedNodeAction({
                        ...currentSelectedNode,
                        data: {
                            ...currentSelectedNode.data,
                            metaObject: metaObject.toJSON()
                        }
                    })
                );
            } catch (error) {
                console.error("Failed to refresh selected node's metaObject after AI reload", error);
            }
        })();
    }, [needsRefresh, appFQN, dispatch, fetchAndPopulateNodes]);

    useEffect(() => {
        const handler = () => dispatch(triggerRefreshAction());
        App.vent.on("ai:reload-graph", handler);
        return () => App.vent.off("ai:reload-graph", handler);
    }, [dispatch]);

    // Add copy-to-app event listener for metadata browser functionality
    useEffect(() => {
        const copyToAppHandler = (id: string) => {
            handleCopyToApp(id, nodes, dispatch);
        };

        App.vent.on("copy-to-app", copyToAppHandler);

        return () => App.vent.off("copy-to-app", copyToAppHandler);
    }, [dispatch, nodes, edges, currentLayout]);

    // Handle node selection from URL or session storage (on reopen)
    useEffect(() => {
        const nodes = getNodes(store.getState());
        if (!nodes || nodes.length === 0) return;

        // Don't override a freshly-created temp node (e.g., sentinel added via Discover PII "+ Sentinel")
        const currentSelectedNode = getSelectedNode(store.getState());
        if (currentSelectedNode?.data?.metaObjectId === "temp-node") return;

        const targetNodeName = nodeName ?? (appName ? getSavedSelection(appName) : null);

        if (targetNodeName) {
            const node = nodes.find(
                n => n.data?.metaObject?.name === targetNodeName || n.data?.name === targetNodeName
            );

            if (node) {
                // Check if this node is already selected to avoid unnecessary updates
                const isAlreadySelected = selectedNode?.id === node.id;
                if (!isAlreadySelected) {
                    // Set selected node in Redux (selected property will be derived via selector)
                    dispatch(setSelectedNodeAction(node));
                    // Open the sidebar
                    dispatch(setIsRightSidebarOpenAction(true));
                }
            } else if (!isFetchingNodes) {
                // Nodes are loaded but the named node wasn't found — clean up the stale URL
                navigateToFlow(appName, flowName);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeName, nodes.length, isFetchingNodes, dispatch]); // Don't include selectedNode to avoid infinite loop

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            // Apply helper lines logic and get snapped changes
            // Dispatch the snapped changes to Redux
            const isPositionChange = changes.some(change => change.type === "position");
            let snappedChanges = changes;
            if (isPositionChange) snappedChanges = onNodeChangeWithHelperLines(changes);
            saveNodeposition(changes);
            dispatch(onNodesChangeAction(snappedChanges));
        },
        [dispatch, onNodeChangeWithHelperLines]
    );
    const onEdgesChange = useCallback((changes: EdgeChange[]) => dispatch(onEdgesChangeAction(changes)), [dispatch]);
    const setEdges = useCallback((edges: Edge[]) => dispatch(setEdgesAction(edges)), [dispatch]);

    const onConnect = useCallback(
        (params: Edge | Connection) => {
            // Build flow component mapping for edge lifting
            const componentToFlowMap = new Map<string, string>();

            nodes.forEach(node => {
                if (node.data?.componentType === "FLOW") {
                    // Try to find objects property in different locations
                    let objects = null;

                    // First try: metaObject.objects (nested)
                    if (node.data?.metaObject?.objects) {
                        objects = node.data.metaObject.objects;
                    }
                    // Second try: metaObject directly (the component data itself)
                    else if (node.data?.metaObject && node.data.metaObject.objects) {
                        objects = node.data.metaObject.objects;
                    }

                    if (objects && typeof objects === "object" && objects !== null) {
                        Object.values(objects).forEach((componentList: any) => {
                            if (Array.isArray(componentList)) {
                                componentList.forEach((componentId: string) => {
                                    componentToFlowMap.set(componentId, node.id);
                                });
                            }
                        });
                    }
                }
            });

            // Apply edge lifting: if source/target is inside a flow, connect to the flow instead
            const actualSource = componentToFlowMap.get(params.source) || params.source;
            const actualTarget = componentToFlowMap.get(params.target) || params.target;

            // Debug: Log edge lifting when it occurs
            if (actualSource !== params.source || actualTarget !== params.target) {
                console.log(
                    `Manual connection edge lifting: ${params.source} -> ${params.target} became ${actualSource} -> ${actualTarget}`
                );
            }

            // Create the edge with the lifted endpoints
            const edgeToAdd = {
                ...params,
                source: actualSource,
                target: actualTarget,
                id: `${actualSource}-${actualTarget}`
            };

            setEdges(addEdge(edgeToAdd, edges));
        },
        [edges, setEdges, nodes]
    );

    const onPaneClick = useCallback(async () => {
        const shouldProceed = promptIfUnsavedChanged ? await promptIfUnsavedChanged() : true;
        if (shouldProceed) {
            if (selectedNode && isTempNode(selectedNode.data)) {
                dispatch(deleteNodeAction(selectedNode.id));
            }
            streamDataLoader.stop();
            dispatch(setStreamGraphPreviewVisibleAction(false));
            dispatch(clearStreamPreviewDataAction());
            dispatch(closeAllSidebarsAction({ appName, flowName }));
        }
    }, [promptIfUnsavedChanged, closeRightSidebar, dispatch, appName, flowName, selectedNode]);

    // Validate connections to prevent Flow-to-Flow connections in Flow view
    const isValidConnection = useCallback(
        (connection: Connection) => {
            if (isViewingSpecificFlow) {
                const sourceNode = nodes.find(node => node.id === connection.source);
                const targetNode = nodes.find(node => node.id === connection.target);

                if (sourceNode?.data?.componentType === "FLOW" && targetNode?.data?.componentType === "FLOW") {
                    return false;
                }
            }

            return true;
        },
        [nodes, isViewingSpecificFlow]
    );

    const showEmptyState = isEmpty && !nodes?.length;

    return (
        <>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                isValidConnection={isValidConnection}
                onNodeClick={onNodeClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                onPaneClick={onPaneClick}
                nodeTypes={NODE_TYPES}
                defaultEdgeOptions={getDefaultEdgeOptions(appStatus)}
                connectionLineType={ConnectionLineType.Bezier}
                fitView
                maxZoom={1}
                nodesConnectable={false}
                panOnScroll={true}
                selectionKeyCode={null}
                selectionMode={SelectionMode.Partial}
                selectionOnDrag={isMultiSelectActive}
                panOnDrag={!isMultiSelectActive}
                proOptions={{ hideAttribution: true }}
            >
                <Background variant={BackgroundVariant.Dots} color="#A7B7C7" />
                {isFetchingNodes && (
                    <Box sx={sxClasses.emptyStateWrapper} data-test-id="flow-loading">
                        <StriimCircularProgress size={24} />
                    </Box>
                )}
                {showEmptyState ? (
                    <FlowEmptyState appStatus={appStatus} />
                ) : (
                    <>
                        <TopRightPanel
                            onNodeSelect={handleNodeSearchWrapper}
                            isMultiSelectActive={isMultiSelectActive}
                            onToggleMultiSelect={toggleMultiSelect}
                        />
                        <BottomLeftPanel />
                    </>
                )}

                <HelperLines
                    horizontal={helperLines.horizontal}
                    vertical={helperLines.vertical}
                    verticalStart={helperLines.verticalStart}
                    verticalEnd={helperLines.verticalEnd}
                />
            </ReactFlow>
        </>
    );
};

export default Flow;
